import { useEffect, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { Input, fieldLabelStyle } from './Input'
import { spacing } from './types'
import type { KeyboardEvent, ReactNode } from 'react'
import { useDebounce } from '@/lib/helpers'

export interface ComboboxOption {
  label: string
  value: string
  description?: string
}

export interface ComboboxProps {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  /** Left icon shown in the input. Defaults to a search icon. */
  icon?: ReactNode
  /** Static list of choices, filtered client-side as the user types. */
  options?: Array<ComboboxOption>
  /** Async suggestion source (debounced), e.g. a remote address search. */
  loadOptions?: (query: string) => Promise<Array<ComboboxOption>>
  /** Minimum typed characters before `loadOptions` fires. */
  minChars?: number
  /**
   * When true, `value`/`onChange` track the raw typed text (address-style:
   * any text is valid, suggestions just offer a shortcut).
   * When false (default), `value` must match one of `options[].value` —
   * typing only filters, and nothing is committed until a row is chosen.
   */
  freeText?: boolean
}

export function Combobox({
  label,
  value,
  onChange,
  placeholder,
  required,
  icon = <Search size={18} />,
  options,
  loadOptions,
  minChars = 2,
  freeText = false,
}: ComboboxProps) {
  const labelFor = (v: string) =>
    options?.find((o) => o.value === v)?.label ?? ''
  const [query, setQuery] = useState(() => (freeText ? value : labelFor(value)))
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const [asyncOptions, setAsyncOptions] = useState<Array<ComboboxOption>>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const debouncedQuery = useDebounce(query, 350)

  useEffect(() => {
    if (!freeText) setQuery(labelFor(value))
  }, [value, options, freeText])

  useEffect(() => {
    if (!loadOptions) return
    if (debouncedQuery.length < minChars) {
      setAsyncOptions([])
      setOpen(false)
      return
    }
    let cancelled = false
    setLoading(true)
    loadOptions(debouncedQuery)
      .then((results) => {
        if (cancelled) return
        setAsyncOptions(results)
        setOpen(results.length > 0)
        setHighlighted(0)
      })
      .catch(() => {
        if (!cancelled) setAsyncOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, loadOptions, minChars])

  const revert = () => {
    if (!freeText) setQuery(labelFor(value))
    setOpen(false)
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        revert()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [value, options, freeText])

  const displayOptions = loadOptions
    ? asyncOptions
    : (options ?? []).filter((o) =>
        o.label.toLowerCase().includes(query.toLowerCase()),
      )

  const select = (opt: ComboboxOption) => {
    onChange(opt.value)
    if (!freeText) setQuery(opt.label)
    if (loadOptions) setAsyncOptions([])
    setOpen(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      revert()
      return
    }
    if (!open || displayOptions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((h) => Math.min(h + 1, displayOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      select(displayOptions[highlighted])
    }
  }

  const spinner = loading ? (
    <span
      style={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 14,
        height: 14,
        border: '2px solid var(--border-soft)',
        borderTopColor: 'var(--ink-2)',
        borderRadius: '50%',
        animation: 'oummaSpin 0.7s linear infinite',
        display: 'inline-block',
      }}
    />
  ) : undefined

  const inner = (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Input
        iconLeft={icon}
        rightSlot={spinner}
        placeholder={placeholder}
        value={freeText ? value : query}
        required={required}
        autoComplete="off"
        onChange={(e) => {
          const v = e.target.value
          if (freeText) {
            onChange(v)
          } else {
            setQuery(v)
            setOpen(true)
            setHighlighted(0)
          }
        }}
        onFocus={() => {
          if (freeText) {
            if (asyncOptions.length > 0) setOpen(true)
          } else {
            setQuery('')
            setOpen(true)
            setHighlighted(0)
          }
        }}
        onKeyDown={handleKeyDown}
      />
      {open && displayOptions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: 'var(--surface-card)',
            border: '1.5px solid var(--border-soft)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          {displayOptions.map((o, i) => (
            <button
              key={o.value}
              type="button"
              onMouseDown={() => select(o)}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                display: 'block',
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                background: i === highlighted ? 'var(--surface-tint)' : 'none',
                border: 'none',
                borderBottom:
                  i < displayOptions.length - 1
                    ? '1px solid var(--border-soft)'
                    : 'none',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-body)',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--ink-0)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {o.label}
              </div>
              {o.description && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-2)',
                    marginTop: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {o.description}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  if (!label) return inner
  return (
    <div>
      <span style={fieldLabelStyle}>{label}</span>
      {inner}
    </div>
  )
}
